import {
  GridColumns,
  GridRenderCellParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import DataGridCell from 'components/common/DataGridCell';
import FileUploader from 'components/common/file-uploader';
import SectionTitle from 'components/common/SectionTitle';
import { currencyFormatter } from 'helpers/currency-formatter';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { transportDocumentService } from 'services/api';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators
} from '../../../../constants';
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import { IPurchaseOrder } from 'interfaces';
import { booleanValueFormatter } from '../data-grid/utils';
import { dateCell } from '../cells/date-cell';
import ConfirmationUnlinkTableDialog from 'components/helpers/ConfirmationGridUnlink';
import { isTechnicianCoordinator } from 'components/helpers/IsTechnicianCoordinator';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isDirector } from 'components/helpers/IsDirector';

export function TransportDocumentColumns(
  isLinkable: boolean,
  onUploadSuccess: any,
  unplugDDTfromInvoice?: (id) => void
): GridColumns {
  const { t } = useTranslation();
  const isDisableEdit = isTechnicianCoordinator() || isTechnician();
  const isDir = isDirector();

  const columns: GridColumns = [
    {
      field: 'ref',
      headerName: t(`entities.ddt.ref`),
      width: 100,
      renderCell: (params: GridRenderCellParams<string>) =>
        isLinkable ? (
          <NavLink to={`./${params.row?.id}`}>
            <SectionTitle>{params.value}</SectionTitle>
          </NavLink>
        ) : (
          <SectionTitle>{params.value}</SectionTitle>
        )
    },
    {
      field: 'billable',
      headerName: t(`entities.ddt.billable`),
      width: 100,
      filterOperators: booleanFilterOperators,
      editable: true,
      type: 'boolean',
      renderCell: booleanCell(t),
      hide: isDir
    },
    {
      field: 'closed',
      headerName: t(`entities.ddt.invoiced`),
      width: 100,
      filterOperators: booleanFilterOperators,
      renderCell: booleanCell(t),
      editable: true,
      type: 'boolean',
      hide: isDir
    },
    {
      field: 'date',
      headerName: t(`entities.ddt.date`),
      filterOperators: dateFilterOperators,
      width: 150,
      renderCell: dateCell
    },
    {
      field: 'date_arrival',
      headerName: t(`entities.ddt.date_arrival`),
      filterOperators: dateFilterOperators,
      width: 150,
      renderCell: dateCell
    },
    // {
    //   field: 'credit_memo_amount',
    //   headerName: t(`entities.ddt.credit_memo_amount`),
    //   filterOperators: numberFilterOperators,
    //   width: 100,
    //   valueFormatter: (params: GridValueFormatterParams) => {
    //     return currencyFormatter(params.value);
    //   },
    //   renderCell: currencyCell
    // },
    {
      field: 'order',
      headerName: t(`entities.ddt.order`),
      filterOperators,
      width: 150,
      valueFormatter: (params) => params.value?.ref ?? '',
      renderCell: (params: GridRenderCellParams<IPurchaseOrder>) => {
        const order = params.value;
        return (
          order &&
          (isLinkable ? (
            <NavLink to={`/tenant/orders/view/${order.id}`}>
              <SectionTitle>{order.ref}</SectionTitle>
            </NavLink>
          ) : (
            <SectionTitle>{order.ref}</SectionTitle>
          ))
        );
      }
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'warehouse_description',
      headerName: t(`global.description`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`entities.ddt.notes`),
      filterOperators,
      width: 200,
      renderCell: baseCell,
      hide: isDir
    },
    {
      field: 'invoice_ref',
      headerName: t(`entities.ddt.invoice_ref`),
      filterOperators,
      width: 150,
      renderCell: baseCell,
      hide: isDir
    },
    {
      field: 'invoice_date',
      headerName: t(`entities.ddt.invoice_date`),
      filterOperators: dateFilterOperators,
      sortable: true,
      width: 150,
      renderCell: dateCell,
      hide: isDir
    }
  ];

  // Removed ', shipping_fare' 18/12/24 for ROMA 2 implementation (consegne splitted from fatturazione)
  ['amount', 'expected_amount'].forEach((colName) => {
    columns.push({
      field: colName,
      headerName: t(`entities.ddt.${colName}`),
      type: 'number',
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value);
      },
      renderCell: (params: GridRenderCellParams<string>) => (
        <DataGridCell content={currencyFormatter(params.value)} />
      ),
      hide: isDir
    });
  });

  if (onUploadSuccess) {
    ['invoice_file', 'ddt_file', 'invoice_revision_file'].forEach((file) => {
      columns.push({
        field: file,
        headerName: t(`entities.ddt.${file}`),
        filterOperators,
        filterable: false,
        sortable: false,
        width: 150,
        valueFormatter: booleanValueFormatter,
        renderCell: (params: GridRenderCellParams<string>) => (
          <FileUploader
            onUploadSuccess={onUploadSuccess}
            service={transportDocumentService}
            entityId={params.row?.id}
            attachmentName={file}
            filename={params.value}
            disableEdit={isDisableEdit}
          />
        )
      });
    });
  }

  if (unplugDDTfromInvoice) {
    columns.push({
      field: 'actions',
      headerName: t(`global.actions`),
      filterOperators,
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (params: GridRenderCellParams<string>) => (
        <ConfirmationUnlinkTableDialog id={params.row?.id} handleUnlink={unplugDDTfromInvoice} />
      )
    });
  }

  return columns;
}
